import React, { useState, useEffect, useCallback } from 'react';
import { Settings, FileText, ChevronRight, HardHat, BookOpen, ChevronDown, Plus, X, Filter } from 'lucide-react';
import useEhCompacto from '../../../utils/useEhCompacto';
import CertificacaoVersoes from './CertificacaoVersoes';
import CertificacaoQuestionario from './CertificacaoQuestionario';
import CertificacaoHistorico from './CertificacaoHistorico';
import AtivosCadastro from './AtivosCadastro';
import { perfilNoModulo } from '../../../utils/perfisDerivados';

// =====================================================================
// Sub-hub "Programa de Certificação" (Módulo Manutenção).
// A tela inicial é a de "Relatórios gravados" (CertificacaoHistorico, a tabela
// dinâmica por loja). Os botões de ação vivem no cabeçalho dela:
//   - "Novo Diagnóstico" → pop-up com os formulários publicados (escolher para
//     preencher) + o manual "como funciona".
//   - "Equipamentos" e "Gerenciar versões" → só Manutenção/Admin/Diretoria.
// =====================================================================

const API = '/api/manutencao/certificacao';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

// `onVoltar` / `rotuloVoltar`: o caminho de volta para a FRENTE do hub que
// contém este programa (hoje "Manutenção Equipamento"). Vem de fora porque o
// sub-hub não sabe — nem deve saber — em que frente foi encaixado.
const CertificacaoForms = ({ onVoltar, rotuloVoltar }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const perfil = perfilNoModulo(user?.perfil, 'manutencao') || '';
  const podeGerenciarVersoes = ['Admin', 'Manutencao', 'Diretoria'].includes(perfil);
  const podePreencher = ['Admin', 'Manutencao', 'Diretoria'].includes(perfil);

  const [tela, setTela] = useState('lista');
  const [templateSel, setTemplateSel] = useState(null);
  const [rascunhoSel, setRascunhoSel] = useState(null); // id do rascunho a retomar (ou null = novo)
  const [modalNovo, setModalNovo] = useState(false);    // pop-up "Novo Diagnóstico"
  const [versoes, setVersoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  // Carrega os formulários publicados só quando o pop-up abre. Perfil só-consulta
  // (Gerente) não vê o botão "Novo Diagnóstico", então nunca dispara esta chamada
  // (que voltaria 403 no /versoes-publicadas).
  const carregar = useCallback(async () => {
    setLoading(true); setErro('');
    try {
      const r = await fetch(`${API}/versoes-publicadas`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setVersoes(await r.json());
    } catch (e) {
      setErro('Erro ao carregar os formulários publicados.');
    } finally {
      setLoading(false);
    }
  }, []);

  const abrirNovo = () => { setModalNovo(true); carregar(); };
  // Escolheu um formulário no pop-up → fecha o modal e abre o preenchimento.
  const escolherTemplate = (v) => {
    setTemplateSel(v); setRascunhoSel(null); setModalNovo(false); setTela('preencher');
  };

  if (tela === 'preencher' && templateSel) {
    return <CertificacaoQuestionario idTemplate={templateSel.ID} idRascunho={rascunhoSel}
      onVoltar={() => { setTela('lista'); setTemplateSel(null); setRascunhoSel(null); }} />;
  }
  if (tela === 'versoes') return <CertificacaoVersoes onVoltar={() => setTela('lista')} />;
  if (tela === 'ativos') return <AtivosCadastro onVoltar={() => setTela('lista')} />;

  // Botões de ação do cabeçalho da tela inicial (Relatórios gravados).
  const acoes = (
    <>
      {podePreencher && (
        <button onClick={abrirNovo} style={btnPrimario}>
          <Plus size={16} /> Novo Diagnóstico
        </button>
      )}
      {podeGerenciarVersoes && (
        <button onClick={() => setTela('ativos')} style={btn('#eef2ff', '#4338ca', '#c7d2fe')}>
          <HardHat size={16} /> Equipamentos
        </button>
      )}
      {podeGerenciarVersoes && (
        <button onClick={() => setTela('versoes')} style={btn('#eef2ff', '#4338ca', '#c7d2fe')}>
          <Settings size={16} /> Gerenciar versões
        </button>
      )}
    </>
  );

  // Tela inicial: a própria consulta (recortada por loja no backend para o Gerente).
  // ⚠️ A volta é DESTA tela para a frente do hub. As telas internas (versões,
  // equipamentos, preenchimento) têm o `onVoltar` própria delas, que volta para
  // cá — nunca para fora do programa: sair de um formulário meio preenchido em
  // um clique é como se perde trabalho.
  return (
    <>
      <CertificacaoHistorico acoesExtras={acoes} onVoltar={onVoltar} rotuloVoltar={rotuloVoltar} />
      {modalNovo && (
        <ModalNovoDiagnostico
          versoes={versoes} loading={loading} erro={erro}
          podeGerenciarVersoes={podeGerenciarVersoes}
          onEscolher={escolherTemplate} onFechar={() => setModalNovo(false)}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------
// Pop-up "Novo Diagnóstico": lista dos formulários publicados (clicar =
// iniciar o preenchimento) + o manual "como funciona" recolhível.
// ---------------------------------------------------------------------
const ModalNovoDiagnostico = ({ versoes, loading, erro, podeGerenciarVersoes, onEscolher, onFechar }) => {
  const compacto = useEhCompacto();
  const [manualAberto, setManualAberto] = useState(false);
  const [equipSel, setEquipSel] = useState('');

  // Esc fecha o modal.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFechar]);

  // Equipamentos distintos dos formulários publicados (= NOME da dtbCertEquipamento,
  // que o backend já devolve como EQUIPAMENTO). Alimentam a lista suspensa do filtro.
  const equipamentos = [...new Set(versoes.map((v) => v.EQUIPAMENTO).filter(Boolean))].sort();
  const versoesFiltradas = equipSel ? versoes.filter((v) => v.EQUIPAMENTO === equipSel) : versoes;

  return (
    <div
      onClick={onFechar}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: compacto ? 10 : 24, overflowY: 'auto',
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16, width: '100%', maxWidth: 720, marginTop: compacto ? 8 : 24,
          boxShadow: '0 20px 50px rgba(15,23,42,.25)', overflow: 'hidden',
        }}>
        {/* Cabeçalho do modal */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: compacto ? '14px 16px' : '18px 22px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ padding: 10, background: '#e0e7ff', borderRadius: 10 }}><Plus size={20} color="#4338ca" /></div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Novo Diagnóstico</h3>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#64748b' }}>Escolha o formulário do equipamento para iniciar o diagnóstico (Etapa 1).</p>
          </div>
          <button onClick={onFechar} title="Fechar" style={{
            display: 'inline-flex', padding: 8, background: '#f1f5f9', color: '#475569',
            border: 'none', borderRadius: 8, cursor: 'pointer',
          }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: compacto ? 14 : 22 }}>
          {/* Manual: como adicionar um novo equipamento (passo a passo) — recolhível */}
          <div style={{ border: '1px solid #c7d2fe', background: '#eef2ff', borderRadius: 12, marginBottom: 16, overflow: 'hidden' }}>
            <button onClick={() => setManualAberto((v) => !v)} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px',
              background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 800, color: '#4338ca', fontSize: 14,
            }}>
              <BookOpen size={18} /> Como funciona / adicionar um novo equipamento
              <ChevronDown size={18} style={{ marginLeft: 'auto', transform: manualAberto ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
            </button>
            {manualAberto && (
              <div style={{ padding: '0 18px 16px', fontSize: 13, color: '#334155', lineHeight: 1.65 }}>
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                  <li><strong>Monte o modelo do relatório</strong> — em <strong>Gerenciar versões → Nova versão</strong>: escolha (ou crie) o <strong>tipo</strong> de equipamento (ex.: Forno) e dê um nome à versão (ex.: “v1 — Forno”). Adicione os <strong>sistemas</strong> com o <strong>peso</strong> de cada um e os <strong>componentes</strong> de cada sistema; marque como <strong>bloqueante</strong> os componentes que, se avaliados como Crítico, reprovam a certificação. Por fim, <strong>publique</strong> a versão. <em>(O peso é por sistema — todo componente herda; peso maior pesa mais no Índice de Saúde.)</em></li>
                  <li style={{ marginTop: 6 }}><strong>Cadastre os equipamentos físicos</strong> — em <strong>Equipamentos → Novo equipamento</strong>: informe o <strong>Patrimônio</strong> (único), o tipo, a loja e os dados (fabricante, modelo, série, ano). Cada equipamento real é um cadastro; vários relatórios ao longo do tempo apontam para ele.</li>
                  <li style={{ marginTop: 6 }}><strong>Faça o diagnóstico (Etapa 1)</strong> — na lista abaixo, clique no equipamento publicado: <strong>selecione o equipamento</strong> (a identificação vem do cadastro), avalie cada componente como <strong>Conforme / Desgaste / Crítico</strong>, anexe as <strong>6 fotos ANTES</strong> (obrigatórias) e registre as <strong>não conformidades</strong>. O <strong>Índice de Saúde</strong> aparece ao vivo. Salve (fica “Em diagnóstico”).</li>
                  <li style={{ marginTop: 6 }}><strong>Conclua a certificação (Etapa 2)</strong> — depois do reparo, em <strong>Relatórios gravados</strong>, abra o relatório e clique <strong>Certificar</strong>: registre ações, peças (R$), limpeza, os testes, a calibração, pendências, <strong>fotos DEPOIS</strong> (obrigatórias), parecer e aceite. Ao <strong>Concluir</strong>, o sistema calcula o resultado e gera o <strong>certificado em PDF</strong>.</li>
                </ol>
                <div style={{ marginTop: 10, padding: 10, background: '#fff', border: '1px solid #c7d2fe', borderRadius: 8 }}>
                  <strong style={{ color: '#4338ca' }}>Como o resultado é decidido</strong><br />
                  Pontuação: Conforme = 2 · Desgaste = 1 · Crítico = 0. Índice de Saúde = pontos obtidos ÷ máximos (ponderado pelo peso do sistema).<br />
                  Classificação: ≥95% Excelente · 90–94% Muito Bom · 80–89% Bom · 70–79% Atenção · &lt;70% Crítico.<br />
                  Certificação: ≥85% <strong>Certificado</strong> · 70–84% <strong>Com Restrições</strong> · &lt;70% <strong>Não Certificado</strong>. Um item <strong>bloqueante</strong> Crítico, falha em teste ou risco do especialista <strong>reprova</strong> mesmo com % alto.
                </div>
              </div>
            )}
          </div>

          {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 16 }}>{erro}</div>}

          {loading ? <p style={{ color: '#64748b' }}>Carregando…</p> : versoes.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
              Nenhum formulário publicado.{podeGerenciarVersoes && ' Use "Gerenciar versões" para publicar um.'}
            </div>
          ) : (
            <>
              {/* Filtro de equipamento — lista suspensa com os equipamentos publicados
                  (só aparece quando há mais de um, senão não tem o que filtrar). */}
              {equipamentos.length > 1 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#475569', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>
                    <Filter size={15} /> Equipamento
                  </span>
                  <select value={equipSel} onChange={(e) => setEquipSel(e.target.value)}
                    style={{ flex: 1, padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#fff' }}>
                    <option value="">Todos os equipamentos</option>
                    {equipamentos.map((eq) => <option key={eq} value={eq}>{eq}</option>)}
                  </select>
                </label>
              )}

              {versoesFiltradas.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
                  Nenhum formulário para o equipamento escolhido.
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 12 }}>
                  {versoesFiltradas.map((v) => (
                    <div key={v.ID}
                      onClick={() => onEscolher(v)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 16, padding: 18, background: '#fff',
                        border: '1px solid #e2e8f0', borderRadius: 12, cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#a5b4fc'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}>
                      <div style={{ padding: 12, background: '#e0e7ff', borderRadius: 10 }}><FileText size={22} color="#4338ca" /></div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{v.EQUIPAMENTO} · {v.NOME_VERSAO}</div>
                        {v.DESCRICAO && <div style={{ marginTop: 4, color: '#64748b', fontSize: 14 }}>{v.DESCRICAO}</div>}
                      </div>
                      <ChevronRight size={20} color="#cbd5e1" />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const btn = (bg, color, border) => ({
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px',
  backgroundColor: bg, color, border: `1px solid ${border}`, borderRadius: 8, fontWeight: 700, cursor: 'pointer',
});

// Botão primário "Novo Diagnóstico" — cheio, para ser a ação de destaque.
const btnPrimario = {
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px',
  backgroundColor: '#4f46e5', color: '#fff', border: '1px solid #4f46e5', borderRadius: 8, fontWeight: 800, cursor: 'pointer',
};

export default CertificacaoForms;
